import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Phone, Mail, MapPin, Clock, Send, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import useScrollToTop from '@/hooks/use-scroll-to-top';
import { supabase } from '@/integrations/supabase/client';

const formSchema = z.object({
  name: z.string().min(2, { message: "Le nom doit contenir au moins 2 caractères." }),
  email: z.string().email({ message: "Veuillez entrer un courriel valide." }),
  phone: z.string().optional(),
  subject: z.string().min(3, { message: "Le sujet doit contenir au moins 3 caractères." }),
  message: z.string().min(10, { message: "Votre message doit contenir au moins 10 caractères." })
});

const contactInfo = [
  {
    icon: Phone,
    label: 'Téléphone',
    value: '(418) 928-9113',
    href: 'tel:+14189289113',
  },
  {
    icon: Mail,
    label: 'Courriel',
    value: 'entretienmcbain@outlook.com',
    href: 'mailto:entretienmcbain@outlook.com',
  },
  {
    icon: MapPin,
    label: 'Adresse',
    value: '2212 Boulevard Valcartier, Québec, QC',
  },
  {
    icon: Clock,
    label: 'Heures',
    value: 'Lun – Ven · 8h00 – 17h00',
  },
];

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  useScrollToTop();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    }
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('submit-contact-message', { body: values });
      if (error) throw error;
      toast({
        title: "Message envoyé!",
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
      form.reset();
    } catch (e) {
      toast({
        title: "Erreur lors de l'envoi",
        description: "Veuillez réessayer ou nous appeler au (418) 928-9113.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Minimal Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-brand-green overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('/lovable-uploads/dbbcb2e0-19d5-4263-ad49-da6e5ca7dd56.png')",
              backgroundPosition: "center 65%",
            }}
          />
        </div>
        <div className="container-wide relative z-10 animate-fade-in">
          <p className="text-brand-sage-light font-sans text-sm tracking-[0.2em] uppercase mb-4">
            Entretiens McBain
          </p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground max-w-2xl leading-[1.1]">
            Parlons de votre projet
          </h1>
        </div>
      </section>

      {/* Contact Info Strip */}
      <section className="border-b border-border bg-card">
        <div className="container-wide">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {contactInfo.map((item) => {
              const Icon = item.icon;
              const content = (
                <div className="flex items-center gap-4 py-6 px-2 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green group-hover:bg-brand-green group-hover:text-primary-foreground transition-colors duration-300">
                    <Icon size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-sans uppercase tracking-wider text-muted-foreground mb-0.5">
                      {item.label}
                    </p>
                    <p className="font-sans text-sm font-medium text-foreground">
                      {item.value}
                    </p>
                  </div>
                </div>
              );

              return item.href ? (
                <a key={item.label} href={item.href} className="hover:bg-muted/50 transition-colors">
                  {content}
                </a>
              ) : (
                <div key={item.label}>{content}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content: Form + Map */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form Column */}
            <div className="lg:col-span-3">
              <div className="mb-10">
                <h2 className="font-display text-3xl md:text-4xl text-foreground mb-3">
                  Envoyez-nous un message
                </h2>
                <p className="text-muted-foreground font-sans max-w-lg">
                  Remplissez le formulaire et notre équipe vous répondra dans les 24 heures.
                </p>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-foreground">Nom complet *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Jean Tremblay" className="h-12 bg-card border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-foreground">Courriel *</FormLabel>
                          <FormControl>
                            <Input {...field} type="email" placeholder="jean@exemple.com" className="h-12 bg-card border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-foreground">Téléphone</FormLabel>
                          <FormControl>
                            <Input {...field} type="tel" placeholder="(418) 123-4567" className="h-12 bg-card border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-foreground">Sujet *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Comment pouvons-nous vous aider?" className="h-12 bg-card border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-sans text-foreground">Message *</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={6}
                            placeholder="Parlez-nous de votre projet ou demande..."
                            className="bg-card border-border resize-none"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="primary"
                    size="lg"
                    className="group"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Envoyer le message
                        <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </Button>
                </form>
              </Form>
            </div>

            {/* Map Column */}
            <div className="lg:col-span-2">
              <div className="sticky top-28">
                <div className="rounded-xl overflow-hidden shadow-lg border border-border h-[480px] lg:h-[600px]">
                  <iframe
                    src="https://www.google.com/maps/d/u/0/embed?mid=1kFHj2JD6hShz5VR2AxDlConzI-7dig8&ehbc=2E312F"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Entretiens McBain - Zone de service"
                    className="w-full h-full"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-3 font-sans text-center">
                  Notre zone de service dans la région de Québec
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-brand-green relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png')" }}
          />
        </div>
        <div className="container-tight relative z-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl text-primary-foreground mb-6">
            Prêt à commencer?
          </h2>
          <p className="text-brand-stone font-sans text-lg mb-10 max-w-2xl mx-auto">
            Demandez directement un devis pour votre projet d'entretien paysager.
          </p>
          <Button
            href="/quote"
            size="lg"
            className="bg-primary-foreground text-brand-green hover:bg-brand-stone transition-all duration-300 px-8 py-6 text-lg font-medium"
          >
            Demander un devis gratuit
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
